import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ArrowRight, ClipboardCheck } from "lucide-react";

import { Navbar } from "@/components/layout/Nav";
import { Carlos, Ana, Mascot } from "@/components/characters/Characters";
import { useLang } from "@/context/LangContext";
import { COMICS } from "@/data/comics";

const CoverArt = ({ category }) => {
  // Comic-book cover art built from our brand characters
  if (category === "service") {
    return (
      <div className="flex items-end justify-center gap-2">
        <Carlos size={120} mood="wave" />
        <Mascot size={90} />
      </div>
    );
  }
  if (category === "financing") {
    return (
      <div className="flex items-end justify-center gap-2">
        <Ana size={120} />
        <div className="brutal-sm px-3 py-2 bg-canales-yellow font-display font-black rotate-[-8deg]">
          $99/mes
        </div>
      </div>
    );
  }
  if (category === "tempur") {
    return (
      <div className="flex items-end justify-center gap-2">
        <Ana size={110} />
        <Carlos size={110} />
      </div>
    );
  }
  return (
    <div className="flex items-end justify-center gap-2">
      <Ana size={130} />
      <Mascot size={80} />
    </div>
  );
};

export default function Comics() {
  const { lang } = useLang();

  return (
    <div className="min-h-screen bg-halftone" data-testid="comics-root">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl border-2 border-black bg-canales-red text-white flex items-center justify-center">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h1 className="font-display font-black text-4xl">
              {lang === "es" ? "Cómics Canales" : "Canales Comics"}
            </h1>
            <p className="font-body text-neutral-700">
              {lang === "es"
                ? "Historias ilustradas que refuerzan cada examen. Pasa las páginas y aprende jugando."
                : "Illustrated stories that reinforce each exam. Flip the pages and learn by playing."}
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6">
          {COMICS.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              data-testid={`comic-card-${c.slug}`}
              className="brutal overflow-hidden bg-white group"
            >
              <div
                className="relative p-6 flex items-center justify-between gap-4 min-h-[220px]"
                style={{ background: c.cover_color }}
              >
                <div className="text-white max-w-[55%]">
                  <div
                    className="inline-block text-black text-xs font-display font-black uppercase tracking-widest px-2 py-0.5 border-2 border-black rounded-full"
                    style={{ background: c.accent }}
                  >
                    {lang === "es" ? `Nº ${i + 1} · ${c.pages.length} páginas` : `Issue ${i + 1} · ${c.pages.length} pages`}
                  </div>
                  <h3 className="mt-2 font-display font-black text-2xl leading-tight">
                    {c.title[lang]}
                  </h3>
                  <p className="mt-2 font-body text-sm opacity-90">
                    {c.subtitle[lang]}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <CoverArt category={c.category} />
                </div>
                {/* halftone dots */}
                <div
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: "radial-gradient(#fff 1.2px, transparent 1.2px)",
                    backgroundSize: "16px 16px",
                  }}
                />
              </div>
              <div className="p-4 flex flex-wrap items-center justify-between gap-2 border-t-4 border-black">
                <Link
                  to={`/comics/${c.slug}`}
                  data-testid={`comic-open-${c.slug}`}
                  className="brutal-btn bg-canales-yellow text-black px-4 py-2 flex items-center gap-2 font-display font-black"
                >
                  <BookOpen className="w-4 h-4" />
                  {lang === "es" ? "Leer cómic" : "Read comic"}
                </Link>
                <Link
                  to={`/exam/${c.exam_slug}`}
                  data-testid={`comic-exam-${c.slug}`}
                  className="brutal-btn bg-white text-black px-4 py-2 flex items-center gap-2 text-sm"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  {lang === "es" ? "Tomar examen" : "Take exam"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
